// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import styled from 'styled-components';

import { baseSpacing, baseStyles } from '../Base';
import { InputWrapperProps } from './InputWrapper';

const getPadding = (props: InputWrapperProps) => {
  const { sizing, leadingIcon, hasPasswordVisibilityToggleButton } = props;
  return sizing === 'sm'
    ? `0.5rem ${
        hasPasswordVisibilityToggleButton ? '4.125rem' : '1.75rem'
      } 0.5rem ${leadingIcon ? '1.3125rem' : '0.5rem'}`
    : `0.625rem ${
        hasPasswordVisibilityToggleButton ? '4.125rem' : '1.75rem'
      } 0.625rem ${leadingIcon ? '1.3125rem' : '1rem'}`;
};

export const StyledInputWrapper = styled.div<InputWrapperProps>`
  position: relative;
  width: 100%;

  > .ch-icon {
    position: absolute;
    width: 2rem;
    left: -38px;
    position: absolute;
    top: 54%;
    transform: translateY(-50%);
  }

  > input {
    padding: ${(props) => getPadding(props)};
    font-size: ${(props) =>
      props.theme.inputSizes[props.sizing || 'md'].fontSize};
    line-height: ${(props) =>
      props.theme.inputSizes[props.sizing || 'md'].lineHeight};
  }
`;

export const StyledInput = styled.input`
  display: flex;
  align-items: center;
  background-color: ${(props) => props.theme.inputs.bgd};
  border: ${(props) => props.theme.inputs.border};
  border-radius: ${(props) => props.theme.inputs.borderRadius};
  color: ${(props) => props.theme.inputs.fontColor};

  width: 100%;
  outline: none;
  appearance: none;

  &::placeholder {
    color: ${(props) => props.theme.inputs.placeholder};
  }

  &[aria-invalid='true'] {
    border: ${(props) => props.theme.inputs.error.border};
  }

  &:disabled {
    background-color: ${(props) => props.theme.inputs.disabled.bgd};
    color: ${(props) => props.theme.inputs.disabled.fontColor};
  }

  // Hides native clear button
  &::-webkit-search-decoration,
  ::-webkit-search-cancel-button,
  ::-webkit-search-results-button,
  ::-webkit-search-results-decoration {
    display: none;
  }

  &::-ms-clear,
  &::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }

  ${baseSpacing}
  ${baseStyles}
`;

interface ClearProps {
  active: boolean;
}

export const StyledClear = styled.button<ClearProps>`
  position: absolute;
  top: 0;
  right: 0.125rem;
  height: 100%;
  border: none;
  background: none;
  cursor: pointer;
  color: ${(props) => props.theme.inputs.clearBg};
  display: ${(props) => (props.active ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;

  &:active,
  &:focus {
    outline: none;
  }
`;

export const StyledPasswordVisibilityToggleButton = styled(StyledClear)`
  right: 2.125rem;
`;
